import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAgents } from "../../hooks/useAgents";
import {
  Bot,
  Search,
  Plus,
  Edit,
  Sparkles,
  Filter,
  RefreshCw,
  TrendingUp,
  DollarSign,
  Users,
  Settings,
} from "lucide-react";

const AgentsPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const { data: agentsData, isLoading, error, refetch } = useAgents();

  // Get provider color classes
  const getProviderColor = (provider) => {
    const colors = {
      groq: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-700",
      openai:
        "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-700",
      anthropic:
        "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-700",
      google:
        "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-700",
    };
    return (
      colors[provider?.toLowerCase()] ||
      "bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-700"
    );
  };

  // Format model name for display
  const formatModelName = (modelName) => {
    if (!modelName) return "Unknown";
    return modelName
      .replace(/llama-/gi, "Llama ")
      .replace(/gpt-/gi, "GPT-")
      .replace(/claude-/gi, "Claude ")
      .replace(/gemini-/gi, "Gemini ")
      .replace(/-/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Filter agents based on search and status
  const filteredAgents = useMemo(() => {
    if (!agentsData?.agents) return [];

    return agentsData.agents.filter((agent) => {
      const matchesSearch =
        agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus =
        statusFilter === "all" || agent.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [agentsData?.agents, searchQuery, statusFilter]);

  // Helper function for safe currency formatting
  const formatCurrency = (value) => {
    const numValue = Number(value);
    if (isNaN(numValue)) return "$0.00";
    return `$${numValue.toFixed(2)}`;
  };

  // Calculate stats with safe number conversion
  const stats = {
    totalAgents: agentsData?.agents?.length || 0,
    activeAgents:
      agentsData?.agents?.filter((agent) => agent.status === "active").length ||
      0,
    totalCost:
      agentsData?.agents?.reduce(
        (sum, agent) => sum + (Number(agent.today_cost) || 0),
        0
      ) || 0,
  };

  // Handle create agent
  const handleCreateAgent = () => {
    navigate("/admin/agents/add");
  };

  // Handle edit agent
  const handleEditAgent = (agentId) => {
    navigate(`/admin/agents/add?id=${agentId}&tab=1`);
  };

  // Handle test agent
  const handleTestAgent = (agentId) => {
    navigate(`/admin/agents/add?id=${agentId}&tab=3`);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
            <span className="text-lg text-gray-600 dark:text-gray-400">
              Loading agents...
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-xl p-6 text-center">
          <div className="text-red-600 dark:text-red-400 text-4xl mb-4">⚠️</div>
          <h2 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-2">
            Failed to Load AI Agents
          </h2>
          <p className="text-red-600 dark:text-red-400 mb-4">
            {error.message ||
              "Something went wrong while loading your AI agents."}
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-3">
            <Bot className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            AI Agents
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your AI customer service agents and configurations
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => refetch()}
            className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium py-2 px-4 rounded-lg text-sm transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={handleCreateAgent}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg text-sm transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Agent
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search agents by name or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
          />
        </div>

        <div className="sm:w-48 relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
          >
            <option value="all">All Agents</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-gray-500 dark:text-gray-400 font-medium text-sm">
                Total Agents
              </h4>
              <p className="text-3xl font-bold text-gray-800 dark:text-white mt-2">
                {stats.total}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {stats.active} active, {stats.total - stats.active} inactive
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
              <Bot className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-gray-500 dark:text-gray-400 font-medium text-sm">
                Active Agents
              </h4>
              <p className="text-3xl font-bold text-gray-800 dark:text-white mt-2">
                {stats.active}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {stats.total > 0
                  ? Math.round((stats.active / stats.total) * 100)
                  : 0}
                % of total
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-gray-500 dark:text-gray-400 font-medium text-sm">
                Today's Cost
              </h4>
              <p className="text-3xl font-bold text-gray-800 dark:text-white mt-2">
                <p className="text-3xl font-bold text-purple-800 dark:text-purple-400 mt-2">
                  {formatCurrency(stats.totalCost)}
                </p>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Across all agents
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Agents Grid */}
      {filteredAgents.length === 0 ? (
        // Empty State
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
          <Bot className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
            {searchQuery || statusFilter !== "all"
              ? "No agents found"
              : "No AI agents yet"}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {searchQuery || statusFilter !== "all"
              ? "Try adjusting your search or filter criteria."
              : "Create your first AI agent to get started with automated customer support."}
          </p>
          {!searchQuery && statusFilter === "all" && (
            <button
              onClick={handleCreateAgent}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center gap-2 mx-auto"
            >
              <Plus className="w-5 h-5" />
              Create Your First Agent
            </button>
          )}
          {(searchQuery || statusFilter !== "all") && (
            <button
              onClick={() => {
                setSearchQuery("");
                setStatusFilter("all");
              }}
              className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        // Agents Grid
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredAgents.map((agent) => (
            <div
              key={agent.id}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-600 group flex flex-col h-full"
            >
              {/* Card Header */}
              <div className="p-6 pb-4 flex-1">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        agent.status === "active"
                          ? "bg-green-500"
                          : "bg-gray-400"
                      }`}
                    ></div>
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded-lg ${
                        agent.status === "active"
                          ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                          : "bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400"
                      }`}
                    >
                      {agent.status}
                    </span>
                    {agent.hands_off_type && (
                      <span className="text-xs font-medium px-2 py-1 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-700">
                        Strict
                      </span>
                    )}
                  </div>

                  <button
                    onClick={() => handleEditAgent(agent.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    <Settings className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                  </button>
                </div>

                {/* Agent Name & Description */}
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {agent.name}
                </h3>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2 min-h-[2.5rem]">
                  {agent.description || "No description provided"}
                </p>

                {/* Model Info */}
                <div
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${getProviderColor(
                    agent.model_provider
                  )}`}
                >
                  <Bot className="w-4 h-4" />
                  <div>
                    <p className="text-xs font-medium opacity-75">
                      {agent.model_provider?.toUpperCase()}
                    </p>
                    <p className="text-sm font-semibold">
                      {formatModelName(agent.model_name)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Metrics */}
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Users className="w-4 h-4 text-gray-500" />
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                        CHANNELS
                      </span>
                    </div>
                    <p className="text-lg font-bold text-gray-800 dark:text-white">
                      {agent.connected_channels || 0}
                    </p>
                  </div>

                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <DollarSign className="w-4 h-4 text-gray-500" />
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                        TODAY
                      </span>
                    </div>
                    <p className="text-lg font-bold text-gray-800 dark:text-white">
                      {formatCurrency(agent.today_cost)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="p-4 border-t border-gray-100 dark:border-gray-700">
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditAgent(agent.id)}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>

                  <button
                    onClick={() => handleTestAgent(agent.id)}
                    disabled={agent.status !== "active"}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center gap-2 ${
                      agent.status === "active"
                        ? "text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/50 hover:border-blue-300 dark:hover:border-blue-600"
                        : "text-gray-400 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 cursor-not-allowed"
                    }`}
                  >
                    <Sparkles className="w-4 h-4" />
                    Try AI
                  </button>
                </div>
              </div>

              {/* Status Footer */}
              <div className="px-6 py-3 bg-gray-50 dark:bg-gray-750 rounded-b-xl border-t border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>
                    Created {new Date(agent.created_at).toLocaleDateString()}
                  </span>
                  <div className="flex items-center gap-1">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        agent.status === "active"
                          ? "bg-green-400"
                          : "bg-gray-400"
                      }`}
                    ></div>
                    <span className="capitalize">{agent.status}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AgentsPage;
